<template>
  <div
    role="dialog"
    aria-describedby="alert-history-description"
    aria-labelledby="alert-history-title"
    data-state="open"
    class="relative p-6 detail-history-alert"
  >
    <div class="flex flex-col space-y-1.5 text-center sm:text-left">
      <h2
        id="alert-history-title"
        class="text-lg font-semibold leading-none tracking-tight"
      >
        {{ headerTitle }}
      </h2>
      <p id="alert-history-description" class="text-sm text-muted-foreground">
        Complete history and notification log for this alert
      </p>
    </div>

    <div class="space-y-6 py-4">
      <section class="space-y-3">
        <h3 class="font-semibold text-base">
          {{ item.ruleName || "--" }}
        </h3>
        <div class="grid gap-3 sm:grid-cols-2 text-sm">
          <div class="detail-row">
            <span class="text-muted-foreground">Device</span>
            <span>{{ deviceLabel }}</span>
          </div>
          <div class="detail-row" v-if="levelLabel !== '--'">
            <span class="text-muted-foreground">Level</span>
            <span class="badge" :class="levelBadgeClass">{{ levelLabel }}</span>
          </div>
          <div class="detail-row">
            <span class="text-muted-foreground">Status</span>
            <span class="badge" :class="statusBadgeClass">
              {{ item.status || "--" }}
            </span>
          </div>
          <div class="detail-row">
            <span class="text-muted-foreground">Escalated</span>
            <span :class="item.escalated ? 'text-red-600 font-medium' : ''">
              {{ item.escalated ? "Yes" : "No" }}
            </span>
          </div>
          <div class="detail-row">
            <span class="text-muted-foreground">Feedback Recipients</span>
            <span>{{ formatRecipients(item.feedbackRecipientNames) }}</span>
          </div>
          <div class="detail-row">
            <span class="text-muted-foreground">Last Feedback</span>
            <span>{{ formatTimestamp(item.lastFeedbackAt) }}</span>
          </div>
        </div>
      </section>

      <div class="divider"></div>

      <section>
        <div class="flex items-center justify-between mb-3">
          <div>
            <h4 class="font-semibold text-base">Notification History</h4>
            <p class="text-sm text-muted-foreground">
              Showing all notifications sent for this rule/event
            </p>
          </div>
          <span class="text-sm text-muted-foreground">
            {{ pagination.total }} record{{ pagination.total === 1 ? "" : "s" }}
          </span>
        </div>

        <el-table
          v-if="historyItems.length"
          :data="historyItems"
          border
          height="auto"
          v-loading="loading"
          element-loading-background="rgba(15, 23, 42, 0.65)"
        >
          <el-table-column prop="recipientName" label="Recipient" min-width="160">
            <template #default="{ row }">
              <div class="flex flex-col leading-tight">
                <span class="font-medium">{{ row.recipientName || "--" }}</span>
                <span class="text-xs text-muted-foreground">
                  {{ row.recipientId ? "ID: " + row.recipientId : "--" }}
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="deviceKey" label="Device Key" min-width="160">
            <template #default="{ row }">
              {{ row.deviceKey || row.deviceKeys || "--" }}
            </template>
          </el-table-column>
          <el-table-column prop="eventLevel" label="Level" width="140">
            <template #default="{ row }">
              <RenderEventLogLevel :modelValue="resolveEventLevel(row)" />
            </template>
          </el-table-column>
          <el-table-column prop="notifyType" label="Type" width="120">
            <template #default="{ row }">
              {{ row.notifyType || "--" }}
            </template>
          </el-table-column>
          <el-table-column prop="message" label="Message" min-width="240">
            <template #default="{ row }">
              <el-tooltip
                v-if="row.message && row.message.length > 80"
                effect="dark"
                :content="row.message"
                placement="top"
              >
                <span>{{ truncateMessage(row.message) }}</span>
              </el-tooltip>
              <span v-else>{{ row.message || "--" }}</span>
              <div v-if="row.errorMsg" class="text-xs text-red-500 mt-1">
                {{ row.errorMsg }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="Status" width="140">
            <template #default="{ row }">
              <span class="badge" :class="getStatusBadgeClass(row.status)">
                {{ row.status || "--" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="sentAt" label="Sent At" width="190">
            <template #default="{ row }">
              {{ formatTimestamp(row.sentAt) }}
            </template>
          </el-table-column>
        </el-table>
        <el-empty
          v-else
          :image-size="120"
          description="No notification logs yet"
        />

        <div class="flex justify-end mt-4" v-if="pagination.total > 0">
          <el-pagination
            @size-change="handlePageSizeChange"
            @current-change="handlePageChange"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :total="pagination.total"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
          />
        </div>
      </section>
    </div>

    <div class="flex justify-end mt-4">
      <button
        type="button"
        @click="$emit('cancel')"
        class="ring-offset-background focus-visible:outline-hidden focus-visible:ring-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground border h-10 px-4 py-2"
      >
        Close
      </button>
    </div>

    <button
      type="button"
      @click="$emit('cancel')"
      class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-x h-4 w-4"
      >
        <path d="M18 6 6 18"></path>
        <path d="m6 6 12 12"></path>
      </svg>
      <span class="sr-only">Close</span>
    </button>
  </div>
</template>

<script>
import { enumValueToLabel, EventLogLevel } from "@/enums";
import { getNotificationHistory } from "@/api";
import RenderEventLogLevel from "@/components/RenderEventLogLevel.vue";

export default {
  name: "DetailHistoryAlert",
  components: {
    RenderEventLogLevel,
  },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      historyItems: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  computed: {
    headerTitle() {
      return this.item.ruleName || this.item.assetName || "Alert Details";
    },
    deviceLabel() {
      const name = this.item.assetName || "--";
      const key = this.item.deviceKey || this.item.deviceKeys || "--";
      return `${name}${key !== "--" ? ` (${key})` : ""}`;
    },
    levelLabel() {
      if (typeof this.item.eventLevel === "undefined") {
        return "--";
      }
      const label = enumValueToLabel(EventLogLevel, this.item.eventLevel);
      return label === "--" ? this.item.eventLevel : label;
    },
    levelBadgeClass() {
      if (this.item.eventLevel === 0) {
        return "badge-low";
      }
      if (this.item.eventLevel === 1) {
        return "badge-medium";
      }
      return "badge-high";
    },
    statusBadgeClass() {
      return this.getStatusBadgeClass(this.item.status);
    },
  },
  watch: {
    item: {
      handler() {
        this.pagination.page = 1;
        this.fetchHistory();
      },
      deep: false,
      immediate: true,
    },
  },
  methods: {
    async fetchHistory() {
      if (!this.item || (!this.item.ruleId && !this.item.eventId)) {
        this.historyItems = [];
        this.pagination.total = 0;
        return;
      }

      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
        };
        if (this.item.ruleId) {
          params.ruleId = this.item.ruleId;
        }
        if (this.item.eventId) {
          params.eventId = this.item.eventId;
        }
        if (this.item.deviceKey) {
          params.deviceKey = this.item.deviceKey;
        }

        const response = await getNotificationHistory(params);
        const list = (response && response.list) || [];
        this.historyItems = list;
        this.pagination.total =
          response && typeof response.totalCount === "number"
            ? response.totalCount
            : list.length;
        if (response && typeof response.currPage === "number") {
          this.pagination.page = response.currPage;
        }
        if (response && typeof response.pageSize === "number") {
          this.pagination.pageSize = response.pageSize;
        }
      } catch (error) {
        console.error("Failed to load notification history", error);
        this.historyItems = [];
        this.pagination.total = 0;
        if (this.$message && typeof this.$message.error === "function") {
          this.$message.error("Failed to load notification history");
        }
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.pagination.page = page;
      this.fetchHistory();
    },
    handlePageSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.fetchHistory();
    },
    formatTimestamp(value) {
      if (!value) {
        return "--";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "--";
      }
      return date.toLocaleString();
    },
    formatRecipients(names) {
      if (!names) {
        return "--";
      }
      return names;
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case "SENT":
          return "badge-success";
        case "FAILED":
          return "badge-danger";
        case "PENDING":
          return "badge-warning";
        default:
          return "badge-muted";
      }
    },
    truncateMessage(message, limit = 80) {
      if (!message || message.length <= limit) {
        return message || "--";
      }
      return message.slice(0, limit) + "...";
    },
    resolveEventLevel(row) {
      if (!row) {
        return undefined;
      }
      if (row.eventLevel !== null && typeof row.eventLevel !== "undefined") {
        return row.eventLevel;
      }
      if (row.level !== null && typeof row.level !== "undefined") {
        return row.level;
      }
      return undefined;
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.divider {
  height: 1px;
  width: 100%;
  background: rgba(148, 163, 184, 0.2);
}

.message-preview {
  max-height: 240px;
  overflow: auto;
  white-space: pre-wrap;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 0.5rem;
  padding: 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 0.125rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
}

.badge-low {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
  border-color: rgba(34, 197, 94, 0.4);
}

.badge-medium {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  border-color: rgba(251, 191, 36, 0.4);
}

.badge-high {
  background: rgba(248, 113, 113, 0.15);
  color: #f87171;
  border-color: rgba(248, 113, 113, 0.4);
}

.badge-success {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
  border-color: rgba(34, 197, 94, 0.4);
}

.badge-warning {
  background: rgba(251, 191, 36, 0.15);
  color: #b45309;
  border-color: rgba(251, 191, 36, 0.4);
}

.badge-danger {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
  border-color: rgba(239, 68, 68, 0.4);
}

.badge-muted {
  background: rgba(148, 163, 184, 0.15);
  color: #94a3b8;
  border-color: rgba(148, 163, 184, 0.3);
}
</style>
